import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Alert,
} from "@mui/material";
import { issueCode, checkQuest } from "../../lib/quests";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../components/firebase";

const GLASS = {
  bg: "linear-gradient(135deg, rgba(18,22,28,.92) 0%, rgba(10,13,17,.88) 100%)",
  border: "1px solid rgba(255,255,255,.08)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const MIN_WAIT_MS = 75_000; // синхронизировано с lib/quests.js
const lsKey = (uid, qid) => `quest:${uid}:${qid}:startedAt`;

export default function PromoQuestDialog({ open, onClose, promo, user }) {
  const [authUser] = useAuthState(auth);
  const quest = useMemo(() => promo?.quests?.[0], [promo]);

  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState(null);
  const [tweetUrl, setTweetUrl] = useState("");

  // локальное состояние обратного отсчёта
  const [waitLeft, setWaitLeft] = useState(0);


  useEffect(() => {
  if (!open) return;
  // Сброс прилипшего состояния при каждом открытии диалога или смене квеста
  setMsg(null);
  setCode("");
  setTweetUrl("");
  setBusy(false);
  // Пересчёт обратного отсчёта для текущего квеста и пользователя
  recomputeWait();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [open, quest?.id, authUser?.uid]);

  // пересчитать обратный отсчёт из localStorage
  const recomputeWait = () => {
    try {
      if (!authUser || !quest?.id) {
        setWaitLeft(0);
        return;
      }
      const k = lsKey(authUser.uid, String(quest.id));
      const started = Number(localStorage.getItem(k) || "0");
      if (!started) {
        setWaitLeft(0);
        return;
      }
      const elapsed = Date.now() - started;
      const left = Math.max(0, MIN_WAIT_MS - elapsed);
      setWaitLeft(left);
    } catch {
      setWaitLeft(0);
    }
  };

  useEffect(() => {
    recomputeWait();
    const i = setInterval(recomputeWait, 1000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.uid, quest?.id, open]);

  if (!quest) return null;

  const isX = quest.type.startsWith("x_");
  const isTG = quest.type.startsWith("tg_");
  const needsCode = /_code$/.test(quest.type);

  const openTarget = (issuedCode = "") => {
    if (quest.type === "x_follow" && quest.x?.targetHandle) {
      window.open(`https://x.com/${quest.x.targetHandle}`, "_blank", "noopener");
    } else if (quest.type === "x_retweet" && quest.x?.tweetId) {
      window.open(`https://x.com/i/status/${quest.x.tweetId}`, "_blank", "noopener");
    } else if (quest.type === "x_post_code") {
      const codePart = issuedCode ? ` ${issuedCode}` : " {{CODE}}";
      const text = encodeURIComponent(
        `Starting with @promospay ${quest.hashtag || ""}${codePart}`
      );
      window.open(`https://x.com/intent/tweet?text=${text}`, "_blank", "noopener");
    } else if (quest.type === "tg_join" && quest.tg?.chatUsername) {
      window.open(`https://t.me/${quest.tg.chatUsername}`, "_blank", "noopener");
    } else if (quest.type === "tg_comment_code" && quest.tg?.targetPostLink) {
      window.open(quest.tg.targetPostLink, "_blank", "noopener");
    }
  };



  const onStart = async () => {
    if (!authUser) {
      setMsg({ type: "warning", text: "Please log in to participate." });
      return;
    }
    try {
      setBusy(true);
      setMsg(null);

      // Всегда отмечаем старт; код выдаём только если нужен
      const issued = await issueCode(quest.id, needsCode);
      if (needsCode) setCode(issued.code || "");

      // Перестраховка: если по каким-то причинам локальный маркер не проставился в issueCode
      try {
        const k = lsKey(authUser.uid, String(quest.id));
        if (!localStorage.getItem(k)) {
          localStorage.setItem(k, String(Date.now()));
        }
      } catch {}

      // сразу обновим UI-таймер и откроем цель
      recomputeWait();
      openTarget(issued.code || "");
    } catch (e) {
      setMsg({ type: "error", text: e?.message || "Failed to start the quest" });
    } finally {
      setBusy(false);
    }
  };

  

  const onCheck = async () => {
    if (!authUser) {
      setMsg({ type: "warning", text: "Log in to your account to check the assignment." });
      return;
    }
    try {
      setBusy(true);
      setMsg(null);
      const proof = {};
      if (quest.type === "x_post_code" && tweetUrl.trim()) {
        proof.tweetUrl = tweetUrl.trim();
      }
      const res = await checkQuest(quest.id, proof);

      if (res?.already) {
        setMsg({ type: "info", text: res.message || "Reward already credited" });
      } else {
        setMsg({ type: "success", text: res?.message || "Done! +15 CrM" });
      }

      // после любой успешной попытки — убираем локальный таймер
      try {
        const k = lsKey(authUser.uid, String(quest.id));
        localStorage.removeItem(k);
      } catch {}
      setWaitLeft(0);
    } catch (e) {
      setMsg({ type: "error", text: e?.message || "The test failed. Try again." });
      recomputeWait();
    } finally {
      setBusy(false);
    }
  };

  const waitLeftSec = Math.ceil(waitLeft / 1000);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          background: GLASS.bg,
          color: GLASS.text,
          border: GLASS.border,
          borderRadius: "12px !important",
          backdropFilter: "blur(8px)",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>{quest.title}</DialogTitle>
      <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,.08)" }}>
        {quest.image && (
          <Box sx={{ borderRadius: 1, overflow: "hidden", mb: 1 }}>
            <img src={quest.image} alt="" style={{ width: "100%", display: "block" }} />
          </Box>
        )}
        <Typography variant="body2" sx={{ color: GLASS.subtext, mb: 1 }}>
          {quest.instructions}
        </Typography>

        {needsCode && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
            <TextField
  fullWidth
  label="Your personal code"
  value={code}
  InputProps={{ readOnly: true }}
  size="small"
  variant="outlined"
  sx={{
    // цвет текста внутри
    "& .MuiInputBase-input": {
      color: "#ffffff",
      fontWeight: 800,
      letterSpacing: 0.6,
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    },
    // лейбл
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,.75)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#c8ffe6" },
    // фон и рамка
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,.06)",
      "& fieldset": { borderColor: "rgba(255,255,255,.18)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,.32)" },
      "&.Mui-focused fieldset": { borderColor: "#14F195" },
    },
  }}
/>
            <Button
              onClick={() => navigator.clipboard.writeText(code)}
              variant="outlined"
              size="small"
              sx={{ textTransform: "none" }}
              disabled={!code}
            >
              Copy
            </Button>
          </Box>
        )}

        {quest.type === "x_post_code" && (
          <TextField
            fullWidth
            label="Link to your tweet (optional)"
            placeholder="https://x.com/you/status/..."
            value={tweetUrl}
            onChange={(e) => setTweetUrl(e.target.value)}
            size="small"
            sx={{ mt: 1 }}
          />
        )}

        {waitLeft > 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Checking... Try again later (≈ {waitLeftSec} sec).
          </Alert>
        )}

        {msg && (
          <Alert severity={msg.type} sx={{ mt: 1 }}>
            {msg.text}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: "none" }}>
          Close
        </Button>
        <Button onClick={onStart} disabled={busy} variant="outlined" sx={{ textTransform: "none" }}>
          Start
        </Button>
        <Button
          onClick={onCheck}
          disabled={busy || waitLeft > 0}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 800,
            background: "linear-gradient(90deg,#2d74d7 0%,#14F195 100%)",
          }}
        >
          {waitLeft > 0 ? `Check (${waitLeftSec}s)` : "Check"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
